/**
 * API plan configuration: quotas, rate limits, prices, unit costs. Pure data with no
 * imports, so it can be unit-tested and shared without pulling in the database layer.
 */

// Whisper transcriptions cost real money (Groq + proxy bandwidth), so they
// consume more quota units than a caption fetch.
export const UNITS_PER_CAPTION_TRANSCRIPT = 1;
export const UNITS_PER_WHISPER_TRANSCRIPT = 15;

export const API_PLANS = {
  // Card-free test tier: enough to integrate and evaluate (≈50 caption
  // transcripts or ~3 Whisper jobs/month), too small to build on without
  // upgrading. One free key per email, email-verified.
  free: {
    name: 'Free',
    monthlyUnits: 50,
    requestsPerMinute: 10,
    priceUsd: 0,
  },
  // Low-commitment entry tier. Competitor entry plans are $5–10/mo (Supadata, TranscriptAPI,
  // youtube-transcript.io); our $29 entry was the objection churned users gave (Sep 2026).
  starter: {
    name: 'Starter',
    monthlyUnits: 1_000,
    requestsPerMinute: 30,
    priceUsd: 9,
  },
  dev: {
    name: 'Developer',
    monthlyUnits: 10_000,
    requestsPerMinute: 60,
    priceUsd: 29,
  },
  scale: {
    name: 'Scale',
    monthlyUnits: 50_000,
    requestsPerMinute: 300,
    priceUsd: 79,
  },
} as const;

export type ApiPlan = keyof typeof API_PLANS;

/**
 * Pay-as-you-go top-up packs. Units never expire and are spent only after the key's monthly plan
 * units run out, so they work on any plan, including Free. Priced ABOVE the subscriptions per unit
 * (Starter $9/1K, Developer $2.90/1K) on purpose: top-ups are for bursts and no-commitment buyers,
 * and must not make a subscription the worse deal. Decided by Noel 2026-09-28.
 */
export const API_TOPUP_PACKS = {
  units_1k: { units: 1_000, priceUsd: 10, label: '1,000 units' },
  units_5k: { units: 5_000, priceUsd: 40, label: '5,000 units' },
  units_20k: { units: 20_000, priceUsd: 120, label: '20,000 units' },
} as const;

export type ApiTopUpPack = keyof typeof API_TOPUP_PACKS;

export function isApiTopUpPack(v: unknown): v is ApiTopUpPack {
  return typeof v === 'string' && Object.prototype.hasOwnProperty.call(API_TOPUP_PACKS, v);
}

/**
 * May a request run? Yes while the key has monthly plan units left, or the account has top-up units.
 */
export function canServeRequest(monthlyUsed: number, monthlyQuota: number, topUpBalance: number): boolean {
  return monthlyUsed < monthlyQuota || topUpBalance > 0;
}

/**
 * How many of a request's units come out of the top-up balance: whatever this request pushes past
 * the monthly plan quota. Monthly units are always spent first.
 *   used 990 of 1,000, +15 (AI) -> 5 from top-up
 *   used 1,200 of 1,000, +1     -> 1 from top-up
 *   used 10 of 1,000, +15       -> 0
 */
export function topUpUnitsFor(monthlyUsed: number, monthlyQuota: number, units: number): number {
  const overBefore = Math.max(0, monthlyUsed - monthlyQuota);
  const overAfter = Math.max(0, monthlyUsed + units - monthlyQuota);
  return overAfter - overBefore;
}
